<template>
  <div class="card card-default">
    <div class="card-header">
      <BsNotification :show="selectedEquipmentCount > 0" type="info">
        <i class="fa fa-lightbulb-on mr-1"></i>
        <span
          v-html="
            this.$t('components/equipments.notifications.stock_equipment_selected', {
              selected: selectedEquipmentCount,
              important: stockSelectedImportantCount
            })
          "
        />
      </BsNotification>
      <BsNotification :show="stockSelectedImportantCount > 10" type="warning">
        <i class="fa fa-exclamation-triangle mr-1"></i>
        <span v-html="tooManySelected" />
      </BsNotification>
      <div class="d-flex flex-row-reverse form-inline">
        <button class="btn btn-secondary" @click="toggleEquipmentView()">
          <span v-if="showOnlySelected">{{ __('equipment.btns.show_all') }}</span>
          <span v-else>{{ __('equipment.btns.show_selected') }}</span>
        </button>
        <SearchBox v-model="searchText" />
      </div>
    </div>
    <div class="card-body">
      <p v-if="displayedEquipmentGroupCount === 0">
        {{ __('equipment.no_feature') }}
      </p>
      <div v-else>
        <EquipmentList
          class="stock"
          :equipments="equipmentGroups"
          :trim-equipment-route="trimEquipmentRoute"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { deburr } from 'lodash'
import EquipmentList from '@components/equipment/EquipmentList.vue'
import SearchBox from '@components/commons/SearchBox.vue'
import BsNotification from '@components/commons/BsNotification.vue'
import { wTrans } from 'laravel-vue-i18n'

export default {
  name: 'StockEquipmentPage',
  components: { EquipmentList, SearchBox, BsNotification },
  props: {
    slug: {
      type: String,
      default: 'used'
    },
    stockId: {
      type: Number,
      default: 0
    },
    equipments: {
      type: [Array, Object],
      default() {
        return {}
      }
    },
    trimEquipments: {
      type: [Array, Object],
      default() {
        return {}
      }
    },
    stockEquipments: {
      type: [Array, Object],
      default() {
        return {}
      }
    },
    trimEquipmentRoute: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      showOnlySelected: false,
      searchText: ''
    }
  },
  computed: {
    tooManySelected() {
      return (
        wTrans('components/equipments.notifications.too_many_stock_equipment_selected', {
          important: this.stockSelectedImportantCount
        })?.value ?? ''
      )
    },
    equipmentGroups() {
      if (this.searchText || this.showOnlySelected) {
        let filteredList = {}
        for (let [group, equipments] of Object.entries(this.$store.state.stockEquipment.list)) {
          filteredList[group] = equipments.filter(x => {
            let selected =
              !this.showOnlySelected ||
              (this.showOnlySelected && (x.isSelected || x.isSelectedInTrim))
            let appearInSearch =
              this.searchText.length === 0 ||
              deburr(x.name.toLowerCase()).includes(deburr(this.searchText))

            return selected && appearInSearch
          })
        }

        return filteredList
      }

      return this.$store.state.stockEquipment.list
    },
    displayedEquipmentGroupCount() {
      let count = 0
      for (let [group, equipments] of Object.entries(this.equipmentGroups)) {
        count += equipments.length
      }
      return count
    },
    stockSelectedEquipmentCount() {
      return this.$store.getters['stockEquipment/stockSelectedEquipmentCount']
    },
    stockSelectedImportantCount() {
      return this.$store.getters['stockEquipment/stockSelectedImportantCount']
    },
    selectedEquipmentCount() {
      return this.$store.getters['stockEquipment/selectedEquipmentCount']
    }
  },
  created() {
    let equipmentGroups = {}

    const stockEquipments = Object.values(this.stockEquipments)
    const trimEquipments = Object.values(this.trimEquipments)

    Object.keys(this.equipments).forEach(group => {
      if (!equipmentGroups[group]) {
        equipmentGroups[group] = []
      }

      const groupEquipments = this.equipments[group]
      groupEquipments.forEach(x => {
        let equipment = {
          id: x.id,
          name: x.name,
          isSelected: false,
          isImportant: false,
          overrideImportant: false,
          isDisabled: false,
          isSelectedInTrim: false,
          isImportantInTrim: false
        }

        stockEquipments.forEach(stockEquipment => {
          if (stockEquipment.id == x.id) {
            equipment.isSelected = stockEquipment.is_checked
            equipment.isImportant = stockEquipment.is_important
            equipment.isDisabled = stockEquipment.is_disabled
          }
        })

        trimEquipments.forEach(trimEquipment => {
          if (trimEquipment.id == x.id) {
            equipment.isSelectedInTrim = trimEquipment.is_checked_in_trim
            equipment.isImportantInTrim = trimEquipment.is_important

            if (equipment.isSelected && equipment.isImportantInTrim !== equipment.isImportant) {
              equipment.overrideImportant = true
            }
          }
        })

        equipmentGroups[group].push(equipment)
      })
    })

    this.$store.dispatch('stockEquipment/setList', equipmentGroups)
  },
  methods: {
    save() {
      const savingProcess = document.getElementById('save-processing')
      const savingSuccess = document.getElementById('save-success')
      const savingError = document.getElementById('save-error')

      savingProcess.style.display = 'block'

      return this.$store
        .dispatch('stockEquipment/persist', this.stockId)
        .then(() => {
          savingProcess.style.display = 'none'
          savingSuccess.style.display = 'block'
        })
        .catch(error => {
          console.error(error)
          savingProcess.style.display = 'none'
          savingError.style.display = 'block'
        })
        .finally(
          setTimeout(() => {
            savingSuccess.style.display = 'none'
            savingError.style.display = 'none'
          }, 3000)
        )
    },
    toggleSelectionView() {
      this.showOnlySelected = !this.showOnlySelected
    },
    toggleEquipmentView() {
      this.$emit('toggleEquipmentSelectionView')
      this.showOnlySelected = !this.showOnlySelected
    }
  }
}
</script>
